<template>
	<div class="time-entry-quick-actions">
		<Transition name="time-entry-show" type="transition">
			<TimeEntryActionsAddEntry
				v-if="showTimeEntryInput"
				class="time-entry-quick-actions__entry"
				:task-id="taskId"
				@close="onTimeEntryCancel()"
			/>
		</Transition>

		<div
			v-if="!showTimeEntryInput && showTrackingIndicator"
			class="time-entry-quick-actions__tracking-wrap"
		>
			<div class="time-entry-quick-actions__tracking-pill">
				<TimeEntryActionsTrackingButton
					:tracking-success-flash="trackingSuccessFlash"
					:tracking-success-leaving="trackingSuccessLeaving"
					:can-complete-tracking="canCompleteTracking"
					:is-waiting-for-active-timer-id="isWaitingForActiveTimerId"
					:show-tracking-duration="showTrackingDuration"
					:tracking-time-primary="trackingTimePrimary"
					:tracking-time-secondary="trackingTimeSecondary"
					:tracking-paused-for-modal="trackingPausedForTimeEntryModal"
					@complete="onCompleteTracking()"
				/>
				<SuiButton
					v-if="showTrackingModifyButton"
					v-tooltip="$t('modifyTimeEntry')"
					class="time-entry-quick-actions__tracking-modify"
					variant="icon"
					tabindex="-1"
					:aria-label="$t('modifyTimeEntry')"
					@click.stop.prevent="onModifyActiveTrackingClick()"
				>
					<SuiIcon name="icon-link-out-16" />
				</SuiButton>
			</div>
		</div>

		<div
			v-else-if="!showTimeEntryInput && showButtons"
			class="time-entry-quick-actions__buttons"
		>
			<SuiButton
				v-tooltip="$t('addTimeEntry')"
				class="time-entry-quick-actions__button"
				variant="icon"
				:aria-label="$t('addTimeEntry')"
				@click="onAddTimeEntryClick()"
			>
				<SuiIcon name="icon-stopwatch-add-16" />
			</SuiButton>

			<SuiButton
				v-if="allowStartTracker"
				v-tooltip="$t('timeTracker_startTracking')"
				class="time-entry-quick-actions__button"
				:aria-label="$t('timeTracker_startTracking')"
				variant="icon"
				@click="onTimeTrackerStart()"
			>
				<SuiIcon name="icon-play-16" />
			</SuiButton>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useMfeUtils } from '@/composables';
import { type Task } from '@/generated/graphql';
import { SuiButton, SuiIcon } from '@developers/scoro-ui';
import TimeEntryActionsAddEntry from './TimeEntryActionsAddEntry.vue';
import TimeEntryActionsTrackingButton from './TimeEntryActionsTrackingButton.vue';
import { useTrackingSuccessFlash } from './useTrackingSuccessFlash';
import {
	DEFAULT_TIME_ENTRY_SECONDS,
	OPTIMISTIC_TRACKING_CLEAR_MS,
	TIME_ENTRY_MODAL_CLOSED_EVENT,
} from '../../constants';
import { formatTrackerDuration } from '@/utils/formatting';
import { useActiveTimer } from '@/features/TimeTracker/composables';

export type TimeEntryQuickActionsProps = {
	taskId: Task['id'];
	showButtons?: boolean;
	allowQuickAdd?: boolean;
	allowStartTracker?: boolean;
	isRequiredFieldsMissing?: boolean;
};

const {
	taskId,
	showButtons = false,
	allowQuickAdd = false,
	allowStartTracker = false,
	isRequiredFieldsMissing = false,
} = defineProps<TimeEntryQuickActionsProps>();

const { rootEmit } = useMfeUtils();

const { activeTimer, dateTimeNow, totalDurationSeconds } = useActiveTimer();

const isTrackingThisTaskFromApollo = computed(
	() => activeTimer.value?.task?.id === taskId
);

const isRequiredFieldsMissingRef = computed(() => isRequiredFieldsMissing);

const {
	trackingSuccessFlash,
	trackingSuccessLeaving,
	frozenDurationSeconds,
	trackingPausedForTimeEntryModal,
	onComplete,
	freezeDuration,
	pauseForModal,
	onModalClosed,
} = useTrackingSuccessFlash({
	isTrackingThisTaskFromApollo,
	totalDurationSeconds,
	isRequiredFieldsMissing: isRequiredFieldsMissingRef,
});

const optimisticTrackingTaskId = ref<Task['id'] | null>(null);
/** Wall clock when optimistic UI began; drives ticker until ActiveTimer arrives */
const optimisticTrackingStartedAtMs = ref<number | null>(null);
let optimisticClearTimeoutId: ReturnType<typeof setTimeout> | undefined;

const isOptimisticForThisTask = computed(
	() => optimisticTrackingTaskId.value === taskId
);

const showTrackingIndicator = computed(
	() =>
		isTrackingThisTaskFromApollo.value ||
		isOptimisticForThisTask.value ||
		trackingSuccessFlash.value ||
		trackingSuccessLeaving.value
);

watch(
	() => activeTimer.value,
	(timer) => {
		if (timer?.task?.id === taskId) {
			optimisticTrackingTaskId.value = null;
			optimisticTrackingStartedAtMs.value = null;
			if (optimisticClearTimeoutId !== undefined) {
				clearTimeout(optimisticClearTimeoutId);
				optimisticClearTimeoutId = undefined;
			}
		}
	},
	{ immediate: true }
);

const canCompleteTracking = computed(
	() =>
		Boolean(activeTimer.value?.id) &&
		!trackingSuccessFlash.value &&
		!trackingPausedForTimeEntryModal.value &&
		isTrackingThisTaskFromApollo.value
);

const isWaitingForActiveTimerId = computed(
	() =>
		showTrackingIndicator.value &&
		!trackingSuccessFlash.value &&
		!activeTimer.value?.id &&
		isOptimisticForThisTask.value
);

const showTrackingDuration = computed(
	() =>
		isTrackingThisTaskFromApollo.value ||
		(isOptimisticForThisTask.value &&
			optimisticTrackingStartedAtMs.value !== null) ||
		(trackingSuccessFlash.value && frozenDurationSeconds.value !== null)
);

const showTrackingModifyButton = computed(
	() =>
		isTrackingThisTaskFromApollo.value &&
		Boolean(activeTimer.value?.id) &&
		!trackingSuccessFlash.value &&
		!trackingPausedForTimeEntryModal.value
);

onMounted(() => {
	addEventListener(TIME_ENTRY_MODAL_CLOSED_EVENT, onModalClosed);
});

onBeforeUnmount(() => {
	removeEventListener(TIME_ENTRY_MODAL_CLOSED_EVENT, onModalClosed);
	if (optimisticClearTimeoutId !== undefined) {
		clearTimeout(optimisticClearTimeoutId);
	}
});

const optimisticElapsedSeconds = computed(() => {
	if (optimisticTrackingStartedAtMs.value === null) {
		return 0;
	}

	if (!isOptimisticForThisTask.value) {
		return 0;
	}

	if (isTrackingThisTaskFromApollo.value) {
		return 0;
	}

	return Math.max(
		0,
		Math.floor(
			(dateTimeNow.value - optimisticTrackingStartedAtMs.value) / 1000
		)
	);
});

const effectiveDurationSeconds = computed(() => {
	if (frozenDurationSeconds.value !== null) {
		return frozenDurationSeconds.value;
	}

	if (isTrackingThisTaskFromApollo.value) {
		return totalDurationSeconds.value;
	}

	return optimisticElapsedSeconds.value;
});

const trackerDisplay = computed(() =>
	formatTrackerDuration(effectiveDurationSeconds.value)
);
const trackingTimePrimary = computed(() => trackerDisplay.value.primary);
const trackingTimeSecondary = computed(() => trackerDisplay.value.secondary);

const onTimeTrackerStart = () => {
	optimisticTrackingTaskId.value = taskId;
	optimisticTrackingStartedAtMs.value = Date.now();
	if (optimisticClearTimeoutId !== undefined) {
		clearTimeout(optimisticClearTimeoutId);
	}

	optimisticClearTimeoutId = setTimeout(() => {
		if (isOptimisticForThisTask.value) {
			optimisticTrackingTaskId.value = null;
			optimisticTrackingStartedAtMs.value = null;
		}

		optimisticClearTimeoutId = undefined;
	}, OPTIMISTIC_TRACKING_CLEAR_MS);

	const timer = activeTimer.value;
	let completePreviousTracker:
		| { timeEntryId: string; openModalOnly: boolean }
		| undefined;

	if (timer?.id && timer.task?.id && timer.task.id !== taskId) {
		completePreviousTracker = {
			timeEntryId: timer.id,
			openModalOnly: Boolean(timer.task.isRequiredFieldsMissing),
		};
	}

	rootEmit('start-time-tracker', {
		taskId: taskId,
		completePreviousTracker,
	});
};

const onCompleteTracking = () => {
	const timeEntryId = activeTimer.value?.id;
	if (!timeEntryId) {
		return;
	}

	const { openModalOnly } = onComplete();
	rootEmit('complete-active-time-tracker', { timeEntryId, openModalOnly });
};

const onModifyActiveTrackingClick = () => {
	const timeEntryId = activeTimer.value?.id;
	if (
		!timeEntryId ||
		!isTrackingThisTaskFromApollo.value ||
		trackingPausedForTimeEntryModal.value
	) {
		return;
	}

	freezeDuration();
	pauseForModal();

	rootEmit('modify-time-entry', {
		taskId: taskId,
		duration: 0,
		isCompleted: true,
		timeEntryId,
	});
};

const showTimeEntryInput = ref(false);

const onAddTimeEntryClick = async () => {
	if (allowQuickAdd) {
		showTimeEntryInput.value = true;
	} else {
		rootEmit('modify-time-entry', {
			taskId: taskId,
			duration: DEFAULT_TIME_ENTRY_SECONDS,
			isCompleted: true,
		});
	}
};

const onTimeEntryCancel = () => {
	showTimeEntryInput.value = false;
};
</script>

<style lang="scss">
.time-entry-quick-actions {
	$parent: &;

	display: flex;
	flex-direction: column;
	align-items: center;
	position: relative;
	width: 100%;
	height: 4rem;

	&__entry {
		position: absolute;
		left: 50%;
		width: calc(8rem + 2rem);
		max-width: calc(8rem + 2rem);
		transform: translateX(-50%);
	}

	&__buttons {
		display: grid;
		grid-template-columns: repeat(2, 4rem);
		justify-content: center;
	}

	&__button.time-entry-quick-actions__button {
		--sui-button-hover-background-color: #{$neutral-20};
		--sui-button-h-padding: 1rem;

		transition: all 0.2s ease;
	}

	&__tracking-wrap {
		display: flex;
		align-items: center;
		justify-content: center;
	}

	&__tracking-pill {
		position: relative;
		display: flex;
		width: calc(8rem + 2rem);
		max-width: calc(8rem + 2rem);
		align-items: center;
		justify-content: center;
		margin-left: -1rem;
		margin-right: -1rem;

		&:hover,
		&:focus-within {
			#{$parent}__tracking-modify#{$parent}__tracking-modify {
				pointer-events: auto;
				opacity: 1;
			}
		}
	}

	&__tracking-modify#{$parent}__tracking-modify {
		--sui-button-background-color: #{$blue-10};
		--sui-button-hover-background-color: #{$blue-20};
		--sui-button-v-padding: 0.5rem;
		--sui-button-h-padding: 0.5rem;
		--sui-button-icon-color: #{$blue-50};
		--sui-button-hover-icon-color: #{$blue-60};

		position: absolute;
		top: -1.25rem;
		right: -1.25rem;
		z-index: 2;
		border-radius: 2rem;
		box-shadow: 0 0 0 0.25rem #{$neutral-00};
		transition: opacity 0.15s ease;
		pointer-events: none;
		opacity: 0;
	}
}

.time-entry-show-enter-active,
.time-entry-show-leave-active {
	transition:
		opacity 0.2s ease-in-out,
		transform 0.2s ease-in-out;
}

.time-entry-show-enter-from {
	opacity: 0;
	transform: translate(-50%, -1rem);
}

.time-entry-show-enter-to {
	opacity: 1;
	transform: translate(-50%, 0);
}

.time-entry-show-leave-from {
	opacity: 1;
	transform: translate(-50%, 0);
}

.time-entry-show-leave-to {
	opacity: 0;
	transform: translate(-50%, -1rem);
}
</style>
